import {
  type AuthoritativeConvictionChartPresentationSource,
  type AuthoritativeConvictionChartPresentationThreshold,
  type ConsensusThresholdCurve,
  type ConsensusThresholdInterpolation,
  createAuthoritativeConvictionChartPresentationSampler,
  createConsensusThresholdCurve,
  type ProjectMarketResolutionAtResult,
  type ServerClock,
} from '@morfi/backend-sdk-v2/client';
import type {
  LivelineCanonicalFrame,
  LivelineFramePaintStatus,
  LivelineFrameSource,
  LivelinePaintedFrame,
} from '@morfi/chart';
import type {
  PaintedMarketShareDisplayStore,
  PaintedMarketShareSessionIdentity,
} from './painted-market-share-display';

export const LIVE_CHART_TAIL_MORPH_MS = 1_000;
export const DEGRADED_CHART_OPACITY = 0.55;
export const CONSENSUS_THRESHOLD_INTERPOLATION: ConsensusThresholdInterpolation = 'monotone';

export interface AuthoritativeTerminalPaintAck {
  readonly sessionKey: string;
  readonly terminalGeometryRevision: string;
}

interface MarketShareLivelineFrameSourceUpdate {
  readonly source: AuthoritativeConvictionChartPresentationSource | null;
  readonly clock: ServerClock | null;
  readonly degraded: boolean;
  readonly authoritative: boolean;
  readonly paintedDisplay?: {
    readonly store: PaintedMarketShareDisplayStore;
    readonly identity: PaintedMarketShareSessionIdentity;
  };
  readonly terminalSession?: {
    readonly key: string;
    readonly onPainted: (ack: AuthoritativeTerminalPaintAck) => void;
  };
  readonly threshold?: AuthoritativeConvictionChartPresentationThreshold;
}

export interface MarketShareLivelineFrameSource extends LivelineFrameSource {
  update(input: MarketShareLivelineFrameSourceUpdate): void;
}

/** Adapts the SDK's renderer-neutral delayed presentation to Liveline. */
export function createMarketShareLivelineFrameSource(): MarketShareLivelineFrameSource {
  const presentationSampler = createAuthoritativeConvictionChartPresentationSampler();
  let source: AuthoritativeConvictionChartPresentationSource | null = null;
  let threshold: AuthoritativeConvictionChartPresentationThreshold | undefined;
  let thresholdCurve: ConsensusThresholdCurve | null = null;
  let thresholdHistory: {
    readonly source: AuthoritativeConvictionChartPresentationSource;
    readonly threshold: AuthoritativeConvictionChartPresentationThreshold;
    readonly second: number;
    readonly points: ReadonlyArray<{ readonly time: number; readonly value: number }> | null;
  } | null = null;
  let paintedDisplay:
    | {
        readonly store: PaintedMarketShareDisplayStore;
        readonly identity: PaintedMarketShareSessionIdentity;
      }
    | undefined;
  let terminalSession:
    | {
        readonly key: string;
        readonly onPainted: (ack: AuthoritativeTerminalPaintAck) => void;
      }
    | undefined;
  let authoritative = false;
  let acknowledgedTerminalKey: string | null = null;
  let sampledForecast: {
    readonly revision: string;
    readonly headSeconds: number;
    readonly value: ProjectMarketResolutionAtResult | null;
  } | null = null;

  return {
    morphDurationMs: LIVE_CHART_TAIL_MORPH_MS,
    update(input: MarketShareLivelineFrameSourceUpdate): void {
      source = input.source;
      if (threshold !== input.threshold) {
        thresholdCurve = input.threshold?.tradingStartedAt
          ? createConsensusThresholdCurve({
              policy: input.threshold.policy,
              tradingStartedAt: input.threshold.tradingStartedAt,
              resolvedAt: input.threshold.resolvedAt,
              interpolation: CONSENSUS_THRESHOLD_INTERPOLATION,
            })
          : null;
      }
      authoritative = input.authoritative;
      threshold = input.threshold;
      presentationSampler.update({
        source: input.source,
        clock: input.clock,
        degraded: input.degraded,
        ...(input.threshold === undefined ? {} : { threshold: input.threshold }),
      });
      paintedDisplay = input.paintedDisplay;
      terminalSession = input.terminalSession;
      if (terminalSession === undefined) acknowledgedTerminalKey = null;
    },
    getFrame(monotonicNowMs: number): LivelineCanonicalFrame | null {
      const frame = presentationSampler.sample(monotonicNowMs);
      if (frame === null) return null;
      const wholeSecond = Math.floor(frame.headSeconds);
      if (
        source !== null &&
        threshold?.tradingStartedAt !== null &&
        threshold?.tradingStartedAt !== undefined &&
        (thresholdHistory?.source !== source ||
          thresholdHistory.threshold !== threshold ||
          thresholdHistory.second !== wholeSecond)
      ) {
        thresholdHistory = {
          source,
          threshold,
          second: wholeSecond,
          points: thresholdCurve?.series(source.chart.fromMs / 1_000, wholeSecond) ?? null,
        };
      }
      const thresholdPoints =
        thresholdHistory?.source === source && thresholdHistory.threshold === threshold
          ? thresholdHistory.points
          : null;
      const thresholdTail = thresholdCurve?.series(wholeSecond, frame.headSeconds);
      sampledForecast = {
        revision: frame.geometryRevision,
        headSeconds: frame.headSeconds,
        value: frame.forecast,
      };
      return {
        revision: frame.geometryRevision,
        earliestAffectedTime:
          frame.earliestAffectedAtMs === null ? null : frame.earliestAffectedAtMs / 1_000,
        logicalNowSeconds: frame.logicalHeadSeconds,
        nowSeconds: frame.headSeconds,
        series: frame.outcomes.map((outcome) => ({
          id: `outcome-${outcome.outcomeIndex}`,
          data: outcome.data,
          value: outcome.value,
        })),
        ...(thresholdPoints === null || frame.thresholdFraction === undefined
          ? {}
          : {
              referencePoints:
                thresholdTail === null || thresholdTail === undefined || thresholdTail.length < 2
                  ? thresholdPoints
                  : [...thresholdPoints, ...thresholdTail.slice(1)],
            }),
        ...(frame.degraded ? { opacity: DEGRADED_CHART_OPACITY } : {}),
        ...(frame.thresholdFraction === undefined
          ? {}
          : { referenceValue: frame.thresholdFraction }),
      };
    },
    onFramePainted(frame: LivelinePaintedFrame, status: LivelineFramePaintStatus): void {
      const currentSource = source;
      const terminalAtMs = currentSource?.chart.terminalAtMs ?? null;
      const terminalPainted =
        authoritative &&
        currentSource !== null &&
        terminalAtMs !== null &&
        !status.morphing &&
        frame.revision === currentSource.geometryRevision &&
        frame.nowSeconds * 1_000 === terminalAtMs;
      paintedDisplay?.store.publish(paintedDisplay.identity, frame, status, {
        forecast:
          sampledForecast?.revision === frame.revision &&
          sampledForecast.headSeconds === frame.nowSeconds
            ? sampledForecast.value
            : null,
        terminalGeometryRevision: terminalPainted ? frame.revision : null,
      });
      if (currentSource === null) return;
      if (terminalSession === undefined || !terminalPainted) {
        return;
      }
      const terminalKey = `${terminalSession.key}:${frame.revision}`;
      if (acknowledgedTerminalKey === terminalKey) return;
      acknowledgedTerminalKey = terminalKey;
      terminalSession.onPainted({
        sessionKey: terminalSession.key,
        terminalGeometryRevision: frame.revision,
      });
    },
    onVisibilityChange(visible: boolean): void {
      if (!visible) {
        if (paintedDisplay !== undefined) {
          paintedDisplay.store.pause(paintedDisplay.identity);
        }
      }
    },
  };
}
